import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { profileService } from "../services";
import { Input } from "../components/ui/input";
import useUserStore2 from "../stores/userStore2";
import ShortenedText from "../components/ShortenedText";
import { FaEye } from "react-icons/fa";
import { GoCheckCircleFill, GoXCircleFill } from "react-icons/go";
import Modal from "../components/Modal";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import { DataTable } from "../components/DataTable";
import StatusBadge from "../components/table/StatusBadge"

const columns = [
  {
    accessorKey: "membershipId",
    header: "Membership ID",
    cell: ({ row }) => <ShortenedText text={row.getValue("membershipId")} />,
  },
  {
    accessorKey: "communityName",
    header: "Community Name",
    cell: ({ row }) => <span>{row.getValue("communityName")}</span>,
  },
  {
    accessorKey: "groupName",
    header: "Group Name",
    cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
  },
  {
    accessorKey: "role",
    header: "Community Role",
    cell: ({ row }) => {
      const roleMapping = {
        U: "User",
        CA: "Community Admin",
        GL: "Group Leader",
        // Add any other role mappings you need
      };

      const roleAbbreviation = row.getValue("role");
      const roleName = roleMapping[roleAbbreviation] || roleAbbreviation;

      return <span>{roleName}</span>;
    },
  },
  {
    accessorKey: "createDate",
    header: "Date Created",
    cell: ({ row }) => (
      <span>
        {new Date(row.getValue("createDate")).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

const EditProfile = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [formData, setFormData] = useState({
    profileId: "",
    profileName: "",
    isPrimary: false,
  });
  const [loading, setLoading] = useState(false);
  const { user, updateProfiles } = useUserStore2();

  // Find the selected profile from userStore2
  const currentProfile = user?.profiles?.find(
    (profile) => profile.id === profileId
  );

  useEffect(() => {
    if (currentProfile) {
      setFormData({
        profileId: currentProfile.id,
        profileName: currentProfile.name,
        isPrimary: currentProfile.status === "Primary",
      });
    }
  }, [currentProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [reload, setReload] = useState(false);

  const handleSave = async () => {
    try {
      setLoading(true);

      const updatedProfile = await profileService.editProfile({
        EntityId: user.id,
        ProfileId: formData.profileId,
        Name: formData.profileName,
        IsPrimary: formData.isPrimary || undefined,
      });

      if (updatedProfile) {
        // Update the profile in userStore2
        const updatedProfiles = user.profiles.map((profile) =>
          profile.id === formData.profileId
            ? {
                ...profile,
                name: formData.profileName,
                status: formData.isPrimary ? "Primary" : "",
              }
            : profile
        );

        updateProfiles(updatedProfiles);

        toast.success("🎉 Profile updated successfully!", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("❌ Failed to update profile", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (membership) => {
    setSelectedProfile({
      ...membership,
      profileName: currentProfile.name, // Add the profile name from the current profile
    });
    setIsQrModalOpen(true);
  };

  const handleActivateProfile = async (profile) => {
    if (!window.confirm(`Are you sure you want to activate ${profile.name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await profileService.editProfile({
        EntityId: user.id,
        ProfileId: profile.id,
        Status: "A",
      });

      // Update Zustand store
      const updatedProfiles = user.profiles.map((p) =>
        p.id === profile.id ? { ...p, profStatus: "A" } : p
      );

      updateProfiles(updatedProfiles);

      setReload(!reload);
      toast.success("🎉 Profile activated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error activating profile:", error);
      toast.error("❌ Failed to activate profile", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateProfile = async (profile) => {
    if (
      !window.confirm(`Are you sure you want to deactivate ${profile.name}?`)
    ) {
      return;
    }

    try {
      setLoading(true);
      await profileService.editProfile({
        EntityId: user.id,
        ProfileId: profile.id,
        Status: "D",
      });

      // Update Zustand store
      const updatedProfiles = user.profiles.map((p) =>
        p.id === profile.id ? { ...p, profStatus: "D" } : p
      );

      updateProfiles(updatedProfiles);

      setReload(!reload);
      toast.success("🎉 Profile deactivated successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deactivating profile:", error);
      toast.error("❌ Failed to deactivate profile", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = (profile) => {
    const baseActions = [
      {
        label: "View Details",
        icon: <FaEye />,
        onClick: () => handleViewDetails(profile),
        variant: "dropPlain",
      },
    ];

    // Add activate/deactivate action based on status
    if (profile.status === "D") {
      baseActions.push({
        label: "Activate",
        icon: <GoCheckCircleFill />,
        onClick: () => handleActivateProfile(profile),
        variant: "dropGreen",
      });
    } else if (profile.status === "A") {
      baseActions.push({
        label: "Deactivate",
        icon: <GoXCircleFill />,
        onClick: () => handleDeactivateProfile(profile),
        variant: "dropRed",
      });
    }

    return baseActions;
  };

  const tableData =
    currentProfile?.memberships?.map((membership) => ({
      membershipId: membership.membershipId,
      status: membership.status,
      communityName: membership.communityName,
      groupName: membership.groupName,
      role: membership.role,
      createDate: membership.createDate,
      profileId: currentProfile.id, // Add this for the QR code
      original: membership,
    })) || [];
  console.log("tableData: ", tableData);

  if (!currentProfile) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4">Profile not found</h1>
          <Button onClick={() => navigate(-1)}>Go back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="p-8 flex flex-col">
        <div className="bg-white shadow p-6 rounded-lg">
          {/* Header with buttons */}
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              Edit Profile
            </h1>
            <div className="flex gap-3">
              <Button variant="hollowGreen" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
          <hr className="mb-8 mt-0" />

          {/* Three-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Column 1: Profile Picture and Date Created */}
            <div className="flex flex-col items-center space-y-6">
              {/* Profile Picture */}
              <div className="relative">
                <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <QRCodeCanvas value={`${user.id}/${profileId}`} size={150} />
                </div>
              </div>

              {/* Status Badge */}
              <div className="w-full flex justify-center">
                <StatusBadge
                  status={currentProfile?.profStatus === "A" ? "Active"
                    : currentProfile?.profStatus === "S" ? "Suspended"
                    : "Deactivated"
                  }
                />
              </div>

              {/* Date Created */}
              <div className="w-full flex flex-col items-center space-y-1">
                <Label className="">Date Created</Label>
                <div className="text-sm text-gray-700 px-3 py-1.5 bg-gray-50 rounded text-center">
                  {currentProfile?.dateCreated || "Not available"}
                </div>
              </div>
            </div>

            {/* Column 2: First set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>Profile ID</Label>
                <Input
                  name="profileId"
                  value={formData.profileId}
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label>Set As Primary</Label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="isPrimaryYes"
                      name="isPrimary"
                      checked={formData.isPrimary}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, isPrimary: true }))
                      }
                      className="h-5 w-5 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <Label
                      htmlFor="isPrimaryYes"
                      className="text-sm font-medium text-gray-700"
                    >
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="isPrimaryNo"
                      name="isPrimary"
                      checked={!formData.isPrimary}
                      onChange={() =>
                        setFormData((prev) => ({ ...prev, isPrimary: false }))
                      }
                      className="h-5 w-5 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <Label
                      htmlFor="isPrimaryNo"
                      className="text-sm font-medium text-gray-700"
                    >
                      No
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: Second set of form fields */}
            <div className="space-y-4">
              <div>
                <Label>Profile Name</Label>
                <Input
                  name="profileName"
                  value={formData.profileName}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="border p-4 mt-4 w-full">
            <div className="text-base sm:text-lg font-semibold text-[#111827]">
              All Memberships
            </div>
            <DataTable columns={columns} data={tableData} actions={actions} />
          </div>
        </div>
      </div>

      {/* View details Modal */}
      {selectedProfile && (
        <Modal isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
          {/* Header */}
          <div className="flex justify-between items-center pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Membership Details
            </h2>
          </div>

          {/* Top Row - Membership Info */}
          <div className="grid grid-cols-3 bg-red-50 gap-2 text-sm pb-4 mb-4">
            <div className="flex flex-col px-3 py-2 rounded">
              <span className="text-gray-500 text-xs">Membership ID</span>
              <span className="text-gray-900 font-medium">
                <ShortenedText text={selectedProfile.membershipId} />
              </span>
            </div>
            <div className="flex flex-col px-3 py-2 rounded">
              <span className="text-gray-500 text-xs">Date Created</span>
              <span className="text-gray-900 font-medium">
                {new Date(selectedProfile.createDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }
                )}
              </span>
            </div>
            <div className="flex flex-col px-3 py-2 rounded">
              <span className="text-gray-500 text-xs">Status</span>
              <span className="text-gray-900 font-medium">
                <div
                  className={`text-sm py-0.5 px-2 rounded-3xl inline-block ${
                    selectedProfile.status === "A"
                      ? "text-[#16A34A] bg-[#DCFCE7]"
                      : selectedProfile.status === "S"
                      ? "text-[#EF4444] bg-[#FEE2E2]"
                      : "text-[#CA8A04] bg-[#FEF9C3]"
                  }`}
                >
                  {
                    // selectedProfile.status === "A"
                    // ? "Active"
                    // : selectedProfile.status === "S"
                    // ? "Suspended"
                    // : "Deactivated"
                    selectedProfile.status
                  }
                </div>
              </span>
            </div>
          </div>

          {/* Details Rows */}
          <div className="space-y-3 items-left text-left pb-6 border-b">
            {/* Profile Name */}
            <div className="flex items-center">
              <span className="text-gray-500 w-32">Profile Name:</span>
              <span className="text-gray-900 font-medium pl-2">
                {selectedProfile.profileName}
              </span>
            </div>

            {/* Community Name */}
            <div className="flex items-center">
              <span className="text-gray-500 w-32">Community Name:</span>
              <span className="text-gray-900 font-medium pl-2">
                {selectedProfile.communityName}
              </span>
            </div>

            {/* Group Name (conditional) */}
            {selectedProfile.groupName && (
              <div className="flex items-center">
                <span className="text-gray-500 w-32">Group Name:</span>
                <span className="text-gray-900 font-medium pl-2">
                  {selectedProfile.groupName}
                </span>
              </div>
            )}

            {/* Membership Role */}
            <div className="flex items-center">
              <span className="text-gray-500 w-32">Membership Role:</span>
              <span className="text-gray-900 font-medium pl-2">
                {selectedProfile.role === "U"
                  ? "User"
                  : selectedProfile.role === "CA"
                  ? "Community Admin"
                  : selectedProfile.role === "GL"
                  ? "Group Leader"
                  : selectedProfile.role}
              </span>
            </div>
          </div>

          {/* QR Code Section */}
          {/*<div className="pt-4 border-t mt-4">*/}
          {/*  <div className="flex flex-col items-center">*/}
          {/*    <span className="text-gray-500 mb-2">Profile QR Code</span>*/}
          {/*    <QRCodeCanvas*/}
          {/*      value={`${user.id}/${selectedProfile.profileId}`}*/}
          {/*      size={150}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*</div>*/}

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsQrModalOpen(false)}>
              Close
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EditProfile;
